import {
  AutoPopover,
  IconFAEllipsisVertical,
  Table,
  TableColumn,
  TableDefaultCell,
  TableDefaultCellProps,
  VfPopoverMenu,
} from '@rcl/rcl';
import { StagingAggregatorDTO } from 'markets-lib';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

enum MenuItemIds {
  shareContract,
}
export interface ShortContract {
  id: string;
  name: string;
  export: boolean;
}
export interface SelectedRow {
  original: ShortContract;
}

const AggregatorTableCellDetails = ({
  value: id,
  onShareContract,
  ...props
}: TableDefaultCellProps<StagingAggregatorDTO> & {
  onShareContract?: (id: StagingAggregatorDTO['id']) => void;
}) => {
  const { t } = useTranslation();

  const handleSelect = useCallback(
    (itemId) => {
      if (itemId === MenuItemIds.shareContract) {
        onShareContract?.(id);
      }
    },
    [id, onShareContract],
  );
  return (
    <TableDefaultCell alignHorizontal='right' {...props}>
      <AutoPopover component={<IconFAEllipsisVertical css={{ cursor: 'pointer' }} />} anchorPosition='right'>
        <VfPopoverMenu
          key={id}
          items={[{ id: MenuItemIds.shareContract, label: t('contractsPage.share') }]}
          onSelect={handleSelect}
        />
      </AutoPopover>
    </TableDefaultCell>
  );
};

export const AggregatorsTable = ({
  data,
  isSharingOn = true,
  isLoading,
  isRowSelectable = false,
  noHeader = false,
  onClickContractName,
  onRowSelect,
  onShareContract,
  toggleSelectAll,
}: {
  data: StagingAggregatorDTO[];
  isSharingOn?: boolean;
  isLoading?: boolean;
  isRowSelectable?: boolean;
  noHeader?: boolean;
  onClickContractName?: (id: StagingAggregatorDTO['id']) => void;
  onShareContract?: (id: StagingAggregatorDTO['id']) => void;
  onRowSelect?: (props: SelectedRow[]) => void;
  status?: boolean;
  toggleSelectAll?: (props: () => void) => void;
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const cssNoHeader = { margin: 0 };
  const cssProps = !noHeader
    ? { overflow: 'visible' }
    : isRowSelectable
    ? { ...cssNoHeader, 'td:first-of-type': { width: 30 } }
    : cssNoHeader;

  const contractColumns = useMemo(() => {
    const columns = [
      {
        Header: t('contractsPage.name'),
        accessor: ({ name }: StagingAggregatorDTO) => name,
        alignHorizontal: 'left',
        isSortable: true,
        Cell: (props: TableDefaultCellProps<StagingAggregatorDTO>) => (
          <TableDefaultCell
            css={{
              cursor: 'pointer',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
            onClick={
              onClickContractName
                ? () => onClickContractName(props.row.original.id)
                : () => history.push(`/${props.row.original.id}/contracts`)
            }
            title={`${props.row.original.id}`}
            {...props}
          />
        ),
      },
    ] as TableColumn<StagingAggregatorDTO>[];

    if (isSharingOn) {
      columns.push({
        Header: 'Details',
        alignHorizontal: 'right',
        headerHidden: true,
        accessor: ({ id }: StagingAggregatorDTO) => id,
        Cell: (props: TableDefaultCellProps<StagingAggregatorDTO>) => (
          <AggregatorTableCellDetails onShareContract={onShareContract} {...props} />
        ),
      });
    }

    return columns;
  }, [t, history, onClickContractName, isSharingOn, onShareContract]);

  return (
    <Table
      columns={contractColumns}
      css={{ ...cssProps, padding: '0 1em 2em 0' }}
      data={data}
      fontSize='md'
      isLoading={isLoading}
      isRowSelectable={isRowSelectable}
      noDataMessage={t('contractsPage.noContracts')}
      noHeader={noHeader}
      selectedRows={onRowSelect}
      toggleSelectAllRows={toggleSelectAll}
    />
  );
};
